import { StaticLabel } from "PosterEditor/model/poster-project";
import { drawSelectedRect, MapElement } from "../canvas-drawing";
import { Rect } from "PosterEditor/model/poster-types";
import { Point } from "PosterEditor/model/poster-types";
import { pointIsInsideRect } from "../point-utils";
import { convertToRGBA } from "../color-utils";
import { MapDrawingConfig } from "../canvas-drawing";
import { rectWithPadding } from "../rect-utils";
import {
  nearestVerticalSnapLine,
  nearestHorzontalSnapLine,
  SnapLine,
} from "../rect-snapping";

export const drawStaticLabels = ({
  map,
  canvasElement,
  project,
  pointerLocation,
  elementToHighlight,
  selectedElement,
}: MapDrawingConfig): {
  elementUnderPointerLocation: MapElement | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  const ctx = canvasElement.current.getContext("2d");
  const result = drawLabels(
    project.staticLabels,
    map,
    ctx,
    pointerLocation,
    elementToHighlight,
    selectedElement?.staticLabel
  );
  if (result.labelUnderCursor) {
    const mapElement: MapElement = {
      type: "staticLabel",
      rect: result.labelUnderCursor.rect,
      element: result.labelUnderCursor.label,
    };
    return {
      elementUnderPointerLocation: mapElement,
      snapLines: result.snapLines,
    };
  }
  return {
    elementUnderPointerLocation: undefined,
    snapLines: result.snapLines,
  };
};

const drawLabels = (
  labels: StaticLabel[],
  map: any,
  ctx: CanvasRenderingContext2D,
  pointerLocation: Point | undefined,
  elementToHighlight: MapElement | undefined,
  selectedLabel: StaticLabel | undefined
): {
  labelUnderCursor:
    | { label: StaticLabel; rect: Rect; point: Point }
    | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  if (!labels) {
    return {
      labelUnderCursor: undefined,
      snapLines: { vertical: undefined, horiztonal: undefined },
    };
  }
  const points = labels.map((label) => label.position);

  let labelUnderCursor:
    | { label: StaticLabel; rect: Rect; point: Point }
    | undefined = undefined;

  let rects: Rect[] = [];
  let selectedRect: Rect | undefined;

  points.forEach((point, index) => {
    const label = labels[index];
    if (label.style.isHidden !== true) {
      const isSelected = label.id === selectedLabel?.id;
      const labelDrawn = drawLabel(
        ctx,
        label,
        pointerLocation,
        elementToHighlight,
        isSelected
      );
      //Match this label.rect against all order label rects
      if (labelDrawn) {
        if (isSelected) {
          selectedRect = labelDrawn.rect;
        } else {
          rects.push(labelDrawn.rect);
        }
      }
      if (labelDrawn && labelDrawn.isUnderCursor) {
        labelUnderCursor = { label, rect: labelDrawn.rect, point };
      }
    }
  });

  if (selectedRect) {
    const contextRect = ctx.canvas.getBoundingClientRect();
    rects.push({
      x: 0,
      y: 0,
      width: contextRect.width,
      height: contextRect.height,
    });
    const snapVertical = nearestVerticalSnapLine(selectedRect, rects);

    const snapHorizontal = nearestHorzontalSnapLine(selectedRect, rects);

    if (snapVertical) {
      drawVerticalSnapLine(ctx, snapVertical);
    }
    if (snapHorizontal) {
      drawHorizontalSnapLine(ctx, snapHorizontal);
    }
    return {
      labelUnderCursor,
      snapLines: { vertical: snapVertical, horiztonal: snapHorizontal },
    };
  }

  return {
    labelUnderCursor,
    snapLines: { vertical: undefined, horiztonal: undefined },
  };
};

const drawVerticalSnapLine = (
  ctx: CanvasRenderingContext2D,
  snapLine: SnapLine
) => {
  ctx.beginPath();
  ctx.strokeStyle = "white";
  ctx.lineWidth = 1;
  ctx.moveTo(snapLine.position, snapLine.start);
  ctx.lineTo(snapLine.position, snapLine.end);
  ctx.stroke();
};

const drawHorizontalSnapLine = (
  ctx: CanvasRenderingContext2D,
  snapLine: SnapLine
) => {
  ctx.beginPath();
  ctx.strokeStyle = "white";
  ctx.lineWidth = 2;
  ctx.moveTo(snapLine.start, snapLine.position);
  ctx.lineTo(snapLine.end, snapLine.position);
  ctx.stroke();
};

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
};

const emptyLabelString = "label";

const drawLabel = (
  ctx: CanvasRenderingContext2D,
  label: StaticLabel,
  pointerLocation: Point | undefined,
  elementToHighlight: MapElement | undefined,
  isSelected: boolean
): { rect: Rect; isUnderCursor: boolean } | undefined => {
  if (!label || !label.style) return undefined;

  const {
    text,
    style: {
      backgroundColor,
      backgroundOpacity,
      padding,
      font,
      textColor,
      fontSize: relativeFontSize,
      borderRadius,
    },
  } = label;

  const labelText = text.length > 0 ? text : emptyLabelString;

  let isLabelDimmed = false;
  if (
    elementToHighlight?.type === "staticLabel" &&
    (elementToHighlight?.element as StaticLabel).id !== label.id
  ) {
    isLabelDimmed = true;
  }
  ctx.globalAlpha = 1; //isLabelDimmed ? 0.2 : 1;

  ctx.setLineDash([]);

  const fontSize = relativeFontSize * ctx.canvas.height;

  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  const textSize = textSizeInContext(
    labelText,
    font,
    fontSize,
    ctx,
    padding * ctx.canvas.width,
    label.style.lineSpacing ?? 100
  );
  const textWidth = textSize.width;
  const textHeight = textSize.height;

  const x = label.position.x * ctx.canvas.width;
  const y = label.position.y * ctx.canvas.height;
  const paddingHorizontal = padding * ctx.canvas.width;
  const paddingVertical = padding * ctx.canvas.width;
  const labelPosition = { x, y };

  let point: Point;
  if (label.style.alignment === "left") {
    point = labelPosition;
  } else if (label.style.alignment === "right") {
    point = { x: labelPosition.x - textWidth, y: labelPosition.y };
  } else {
    point = { x: labelPosition.x - textWidth / 2, y: labelPosition.y };
  }

  const lines = labelText.split("\n");

  const boxX = point.x - paddingHorizontal;
  const boxY = point.y - paddingVertical;

  const boxWidth = textWidth + paddingHorizontal * 2;
  const boxHeight = textHeight + paddingVertical * 2;

  let isHighlighted = false;
  if (pointerLocation) {
    let rect: Rect = { x: boxX, y: boxY, width: boxWidth, height: boxHeight };
    if (pointIsInsideRect(pointerLocation, rect)) {
      isHighlighted = true;
    }
  }
  ctx.save();

  if (backgroundColor) {
    const opacity = backgroundOpacity ?? 1;
    ctx.fillStyle = convertToRGBA(backgroundColor, opacity);
    drawRoundedRect(ctx, boxX, boxY, boxWidth, boxHeight, borderRadius);
    ctx.fill();
  }

  const rect = { x: boxX, y: boxY, width: boxWidth, height: boxHeight };

  ctx.fillStyle = textColor;

  // Handle multi-line text - calculate line spacing using the same formula as textSizeInContext
  let baseLineHeight = fontSize * 1.2;
  if (lines.length > 0) {
    const textMetrics = ctx.measureText(lines[0]);
    if (
      textMetrics.actualBoundingBoxAscent &&
      textMetrics.actualBoundingBoxDescent
    ) {
      baseLineHeight =
        textMetrics.actualBoundingBoxAscent +
        textMetrics.actualBoundingBoxDescent;
    }
  }

  const lineSpacingSetting = label.style.lineSpacing ?? 100;
  const lineHeightMultiplier = 0.8 + (lineSpacingSetting - 50) / 125;
  const singleLineHeight = baseLineHeight * lineHeightMultiplier;

  // Calculate starting Y position for first line (grows downward)
  const startY = boxY + paddingVertical + baseLineHeight / 2 + baseLineHeight * 0.11;

  lines.forEach((line, index) => {
    const lineY = startY + index * singleLineHeight;
    ctx.fillText(line, point.x, lineY);
  });
  if (isSelected) {
    const padded = rectWithPadding(rect, 10);
    drawSelectedRect(padded, ctx);
  }

  ctx.restore(); // Restore canvas state

  return { rect, isUnderCursor: isHighlighted };
};

// UTILITY

export const labelRectInCanvas = (
  label: StaticLabel,
  ctx: CanvasRenderingContext2D
): Rect => {
  let size = labelSizeInContext(label, ctx);
  const point = label.position;
  const padding = label.style.padding ?? 0;
  const x = point.x - padding;
  const y = point.y - padding;
  return { x, y, width: size.width, height: size.height };
};

export const labelSizeInContext = (
  label: StaticLabel,
  ctx: CanvasRenderingContext2D
): { width: number; height: number } => {
  const fontSize = label.style.fontSize;
  const font = label.style.font;
  const padding = label.style.padding ?? 0;

  const textSize = textSizeInContext(
    label.text,
    font,
    fontSize,
    ctx,
    padding,
    label.style.lineSpacing ?? 100
  );
  const labelWidth = textSize.width + padding * 2;
  const labelHeight = textSize.height + padding * 2;

  return { width: labelWidth, height: labelHeight };
};

//TODO: check OffscreenCanvasRenderingContext2D for map rendering performance.

export const textSizeInContext = (
  text: string,
  font: string,
  fontSize: number,
  ctx: CanvasRenderingContext2D,
  padding: number = 0,
  lineSpacingSetting: number = 100
) => {
  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  const lines = text.split("\n");
  let maxWidth = 0;

  // Find the widest line
  lines.forEach((line) => {
    const lineMetrics = ctx.measureText(line);
    maxWidth = Math.max(maxWidth, lineMetrics.width);
  });

  let baseLineHeight = fontSize * 1.2;
  if (lines.length > 0) {
    const textMetrics = ctx.measureText(lines[0]);
    if (
      textMetrics.actualBoundingBoxAscent &&
      textMetrics.actualBoundingBoxDescent
    ) {
      baseLineHeight =
        textMetrics.actualBoundingBoxAscent +
        textMetrics.actualBoundingBoxDescent;
    }
  }

  // Calculate line height multiplier from lineSpacingSetting (50-200)
  // 50 → 0.8x (very tight, overlap), 100 → 1.2x (default), 200 → 2.0x (very loose)
  const lineHeightMultiplier = 0.8 + (lineSpacingSetting - 50) / 125;
  const singleLineHeight = baseLineHeight * lineHeightMultiplier;
  const lineSpacing = singleLineHeight - baseLineHeight;

  // Total height: base height for all lines + spacing between lines (not after last line)
  const totalHeight =
    baseLineHeight * lines.length + lineSpacing * (lines.length - 1);

  return { width: maxWidth, height: totalHeight };
};
